import React, { useEffect, useRef, useState } from 'react';
import { ThemedText, ThemedView } from '@app/theme';
import { StyleSheet, TextInput  } from 'react-native';
import Info from '../../../../assets/Icons/Info';
import { useIsEnhanceLimit } from '@app/hooks/useIsEnhanceLimit';
import InputText from '@app/sharedComponents/TextInput/TextInput';
import { CheckCircle } from '@app/sharedComponents/Icons';
import { markPanInputInvalid } from '../utils';
const panCardRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

type EditPanFlowProps = {
    panNumber: string | null;
    mobileNumber: string;
    setPanNumber: (data?: any) => void;
    setMobileNumber: (data?: any) => void;
    panEditable: boolean;
    errorModalData: any;
};

const EditPanFlow: React.FC<EditPanFlowProps> = ({
    panNumber,
    mobileNumber,
    setPanNumber,
    setMobileNumber,
    panEditable,
    errorModalData,
}) => {
    const { isFromEnhanceLimit : isFromEnhancedLimit } = useIsEnhanceLimit();
    const panRef = useRef<TextInput>(null);
    const inputRef = useRef<TextInput>(null);
    const [panError, setPanError] = useState<string>('');
    const [hasError, setHasError] = useState<string>('');
    const [isPanValid, setIsPanValid] = useState<boolean>(false);
    const [isMobileNumberValid, setIsMobileNumberValid] = useState<boolean>(false);
    let debounceTimeout: NodeJS.Timeout | null = null; 

    const processInput = (text: string) => {
        let inputText = text.toUpperCase().replace(/[^A-Z0-9]/g, ''); 
        if (inputText.length > 10) {
          inputText = inputText.slice(0, 10); 
        }
        setPanNumber(inputText);
        if (!panCardRegex.test(inputText) && inputText.length === 10) {
          setIsPanValid(false);
          setPanError('Enter a valid PAN');
        } else {
          setIsPanValid(inputText.length === 10);
          setPanError('');
        }
      };

    const onInputPan = (text: string) => {
        const hasLowercase = /[a-z]/.test(text);
      
        if (hasLowercase) {
          if (debounceTimeout) clearTimeout(debounceTimeout);
      
          debounceTimeout = setTimeout(() => {
            processInput(text);
          },0); 
        } else {
          processInput(text);
        }
      };


      const onInputMobileNumber = (text: string) => {
        let formattedText = text.replace(/[^0-9]/g, '');
        if (formattedText.length > 10) {
          formattedText = formattedText.slice(0, 10);
        }
        setMobileNumber(formattedText);
        if (formattedText.length === 10) {
          setHasError('');
          setIsMobileNumberValid(true);
        } else {
          setIsMobileNumberValid(false);
        }
      };

      const onBlur = () => {
        if(inputRef.current){
          inputRef.current?.blur()
        }
        if (mobileNumber.length < 10 && mobileNumber.length > 0) {
          setHasError('Enter a 10 digit mobile number');
        } else {
          setHasError('');
        }
      }

      useEffect(() => {
        if(markPanInputInvalid(errorModalData?.errorCode)) {
            setPanError('Enter a valid PAN');
        }
      }, [errorModalData])

    return (
        <>
            <ThemedView style={ls.bodyContainer} backgroundColor='bg-surface-primary'>
                <ThemedText
                    variant='h3s'
                    style={{ marginBottom: 8 }}
                >
                    {
                        isFromEnhancedLimit ? 'Fetch updated portfolio' : 'Check eligible credit limit'
                    }
                </ThemedText>
                <ThemedText
                    variant='b3r'
                    color='text-secondary'
                    style={{ marginBottom: 32 }}
                >
                    Find out how much credit you can unlock using your investments.
                </ThemedText>
                {/* PAN input field */}
                <InputText
                    autoCapitalize="characters"
                    ref={panRef}
                    placeHolder={'Enter PAN'}
                    keyboardType="default"
                    label="PAN"
                    showLabel={true}
                    editable={panEditable}
                    value={panNumber}
                    onChangeText={onInputPan}
                    showErrorMessage={panEditable || panError ? true : false}
                    showErrorMessageInFocus={true}
                    // isDarkMode={Boolean(isDarkMode)}
                    hasError={panError}
                    successMessage={panEditable ? '' : 'Your PAN is now verified, proceed to get limit.'}
                    showRightAccessory={panNumber && panNumber?.length>0 ? true : false}
                    rightAccessory={() => {
                        return (
                        <>
                            {panNumber?.length === 10 && !panError ? (
                            <>
                                <CheckCircle
                                size={24}
                                weight="fill"
                                fill="rgb(18, 183, 106)"
                                />
                            </>
                            ) : (
                            <ThemedText
                                variant='b2r'
                                color='text-secondary'
                                mr="sm"
                                >
                                {panNumber?.length}/10
                            </ThemedText>
                            )}
                        </>
                        );
                    }}
                />
                <ThemedView style={{ paddingTop: !panEditable ? 4 : 0 }}></ThemedView>
                <InputText
                        ref={inputRef}
                        keyboardType="phone-pad"
                        onChangeText={onInputMobileNumber}
                        onBlur={onBlur}
                        value={mobileNumber}
                        showAnimation
                        showLabel
                        label={'Mobile number'}
                        showLeftAccessory
                        // isDarkMode={Boolean(isDarkMode)}
                        leftAccessory={() => {
                            return (
                                <ThemedView style={{ marginRight: 5 }}>
                                    <ThemedText variant='b2r' color='text-secondary' mr="sm">
                                        +91
                                    </ThemedText>
                                </ThemedView>
                            );
                        }}
                        showRightAccessory={mobileNumber.length >= 1}
                        rightAccessory={() => {
                        return (
                            <ThemedView flexDirection="row" alignItems="center">
                            {mobileNumber.length === 10 ? (
                                <CheckCircle
                                    size={24}
                                    weight="fill"
                                    fill="rgb(18, 183, 106)"
                                />
                            ) : (
                                <ThemedText variant='b2r' color='text-secondary' mr="sm">
                                    {`${mobileNumber?.length}/10`}
                                </ThemedText>
                            )}
                            </ThemedView>
                        );
                        }}
                    showErrorMessage={true}
                    hasError={hasError}
                    maxLength={10}
                />
                <ThemedView
                    flexDirection='row'
                    alignItems='center'
                    backgroundColor='bg-fill-emphasis-primary-1'
                    style={{
                        marginBottom: 16,
                        paddingHorizontal: 16,
                        paddingVertical: 8
                    }}
                    borderRadius='sm'
                >
                    <Info size={24} color='#2D57D9'/>
                      <ThemedView style={{width: '90%', marginLeft: 8}}>
                        <ThemedText style={{lineHeight: 18}}>
                          <ThemedText variant='b4r' color='text-action-primary' fontWeight='400'>
                              Use mobile number linked to your{' '}
                          </ThemedText>
                          <ThemedText variant='b4s' color='text-action-primary' fontWeight='400' style={{fontWeight: '600'}}>
                              mutual fund investments.
                          </ThemedText>
                      </ThemedText>
                    </ThemedView>
                </ThemedView>
            </ThemedView>
        </>
    );
};

export default EditPanFlow; 

const ls = StyleSheet.create({
    bodyContainer: {
        flex: 1,
        paddingHorizontal: 16,
        paddingVertical: 24,
    }
});